import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiCpu,
  FiCloud,
  FiDatabase,
  FiBarChart2,
  FiGlobe,
  FiSmartphone,
} from "react-icons/fi";
import axios from "axios";

function Technology() {
  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTechnologies();
  }, []);

  const fetchTechnologies = async () => {
    try {
      const response = await axios.get(
        "https://agri-back-mauve.vercel.app/api/content/public/technology"
      );
      if (response.data.success) {
        setTechnologies(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching technologies:", error);
    } finally {
      setLoading(false);
    }
  };

  const techCategories = {
    iot: {
      icon: <FiCpu className="w-8 h-8" />,
      color: "from-blue-500 to-cyan-500",
    },
    analytics: {
      icon: <FiDatabase className="w-8 h-8" />,
      color: "from-purple-500 to-pink-500",
    },
    monitoring: {
      icon: <FiBarChart2 className="w-8 h-8" />,
      color: "from-green-500 to-emerald-500",
    },
    platform: {
      icon: <FiGlobe className="w-8 h-8" />,
      color: "from-orange-500 to-red-500",
    },
    mobile: {
      icon: <FiSmartphone className="w-8 h-8" />,
      color: "from-indigo-500 to-purple-500",
    },
    cloud: {
      icon: <FiCloud className="w-8 h-8" />,
      color: "from-cyan-500 to-blue-500",
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading technologies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Our Technology Stack
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl md:text-2xl max-w-3xl mx-auto"
          >
            Cutting-edge technologies powering the future of African agriculture
          </motion.p>
        </div>
      </section>

      {/* Technologies Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {technologies.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">💻</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                No technologies listed yet
              </h3>
              <p className="text-gray-600">
                Check back later for our technology solutions
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {technologies.map((tech, index) => (
                <motion.div
                  key={tech._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div
                    className={`w-16 h-16 mb-6 bg-gradient-to-r ${
                      techCategories[tech.category]?.color ||
                      "from-gray-500 to-gray-700"
                    } rounded-2xl flex items-center justify-center text-white`}
                  >
                    {techCategories[tech.category]?.icon || (
                      <FiCpu className="w-8 h-8" />
                    )}
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {tech.title}
                  </h3>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {tech.description}
                  </p>

                  {tech.features && tech.features.length > 0 && (
                    <ul className="space-y-2">
                      {tech.features.slice(0, 4).map((feature, i) => (
                        <li
                          key={i}
                          className="flex items-center text-sm text-gray-600"
                        >
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Tech Innovation Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
            >
              Driving Agricultural Innovation
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-gray-600 mb-8"
            >
              Our technology stack combines IoT sensors, data analytics, machine
              learning, and mobile platforms to deliver comprehensive
              agricultural solutions tailored for African farming conditions.
            </motion.p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Technology;
